import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/MenuRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import Diversity3RoundedIcon from '@mui/icons-material/Diversity3Rounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { fetchStructure, slugify } from '../api/structure';
import { DataState } from '../components/DataState';
import { useAuth } from '../auth/useAuth';
import { palette } from '../theme';

const DRAWER_WIDTH = 264;

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const structureQuery = useQuery({
    queryKey: ['structure'],
    queryFn: ({ signal }) => fetchStructure(signal),
  });

  const pathParts = location.pathname.split('/').filter(Boolean);
  const activeLayerSlug = pathParts[0] === 'dashboard' ? pathParts[1] : undefined;
  const activeAreaSlug = pathParts[0] === 'dashboard' ? pathParts[2] : undefined;

  const [expanded, setExpanded] = useState<string | null>(null);
  useEffect(() => {
    if (activeLayerSlug) setExpanded(activeLayerSlug);
  }, [activeLayerSlug]);

  if (!user) return null;

  const canSeeRunsHealth = user.role === 'admin' || user.tabs.includes('runs-health');
  const canSeeContentRadar = user.role === 'admin' || user.tabs.includes('content-radar');
  const canSeeMetaAds = user.role === 'admin' || user.tabs.includes('meta-ads');

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 3, gap: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            bgcolor: palette.sage[500],
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          n
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.1 }}>
            natu.care
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            KPI Dashboard
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
        <DataState
          loading={structureQuery.isLoading}
          error={structureQuery.error}
          data={structureQuery.data}
          isEmpty={(d) => d.layers.length === 0}
          emptyMessage={
            user.role === 'admin'
              ? 'No dashboard data yet. Run the sync to populate Layers and Areas.'
              : 'No dashboard sections available to you — ask an admin for access.'
          }
        >
          {(data) =>
            data.layers.map((layer) => {
              const layerSlug = slugify(layer.layer);
              return (
                <Accordion
                  key={layerSlug}
                  expanded={expanded === layerSlug}
                  onChange={() => setExpanded((prev) => (prev === layerSlug ? null : layerSlug))}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    bgcolor: 'transparent',
                    border: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon fontSize="small" />}
                    sx={{
                      px: 3,
                      minHeight: 44,
                      '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {layer.layer}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pb: 0.5 }}>
                    <List disablePadding>
                      {layer.areas.map((area) => {
                        const areaSlug = slugify(area.area);
                        const path = `/dashboard/${layerSlug}/${areaSlug}`;
                        const selected = activeLayerSlug === layerSlug && activeAreaSlug === areaSlug;
                        return (
                          <ListItem key={areaSlug} disablePadding>
                            <ListItemButton
                              component={NavLink}
                              to={path}
                              selected={selected}
                              onClick={() => setMobileOpen(false)}
                              sx={{ pl: 4 }}
                            >
                              <ListItemText
                                primary={area.area}
                                primaryTypographyProps={{ fontSize: 13, fontWeight: selected ? 600 : 500 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })
          }
        </DataState>
      </Box>
      <>
        <Divider />
        <List sx={{ py: 1 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/"
              selected={location.pathname === '/'}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>
                <SpaceDashboardRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/okrs"
              selected={location.pathname === '/okrs'}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>
                <FlagRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="My OKRs" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/okrs/all"
              selected={location.pathname.startsWith('/okrs/all')}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>
                <Diversity3RoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Everyone's OKRs" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={NavLink}
              to="/instructions"
              selected={location.pathname.startsWith('/instructions')}
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>
                <MenuBookRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Instructions" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
          {user.isManager && (
            <ListItem disablePadding>
              <ListItemButton
                component={NavLink}
                to="/okrs/team"
                selected={location.pathname.startsWith('/okrs/team')}
                onClick={() => setMobileOpen(false)}
              >
                <ListItemIcon>
                  <TrackChangesRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Team OKRs" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
              </ListItemButton>
            </ListItem>
          )}
          {canSeeContentRadar && (
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/content-radar"
                  selected={location.pathname.startsWith('/content-radar')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <RadarRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Content Radar" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            )}
          {canSeeMetaAds && (
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/meta-ads"
                  selected={location.pathname.startsWith('/meta-ads')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <CampaignRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Meta Ads" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            )}
          {canSeeRunsHealth && (
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/dashboard/runs-health"
                  selected={location.pathname.startsWith('/dashboard/runs-health')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <HealthAndSafetyRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Runs & Health" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            )}
            {user.role === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/admin/users"
                  selected={location.pathname.startsWith('/admin/users')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <GroupRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Users" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            edge="start"
            sx={{ display: { md: 'none' } }}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={user.role}
            sx={{ textTransform: 'capitalize', bgcolor: palette.sage[100], color: palette.sage[700] }}
          />
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} size="small">
            <Avatar src={user.picture} alt={user.name} sx={{ width: 32, height: 32 }}>
              {user.name.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {user.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={async () => {
                setMenuAnchor(null);
                await logout();
                navigate('/login', { replace: true });
              }}
            >
              <ListItemIcon>
                <LogoutRoundedIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar />
        <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, maxWidth: 1400, width: '100%', mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
